import React, { useCallback, useContext, useMemo, useState } from "react";
import { useFragment, useRefetchableFragment } from "react-relay";

import { faTriangleExclamation } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faVideo, faWaveformLines } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faRecordSlash } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import cx from "classnames";
import { graphql } from "relay-runtime";

// eslint-disable-next-line max-len
import { useWillBeRecordedNoCacheQuery } from "~/account/Copilot/AutoRecordRules/modal/WillBeRecordedNoCache";
import { UpgradeIcon } from "~/billing";
import { trackConversion } from "~/lib/ab/growthbook";
import { RecordIconSwitch } from "~/meetingRecorder/RecordIcon";
// eslint-disable-next-line max-len
import { FLOATING_RECORDING_CONTROLS_EXPERIMENT_KEY } from "~/meetingRecorder/RecordingControls/FloatingRecordingControls";
import { CallIcon, getCallName } from "~/stream/NoteHeader/JoinConferenceButton";
import { Button, Tooltip } from "~/ui";
import BetaLabel from "~/ui/labels/BetaLabel";
import Menu from "~/ui/menu";

import MeetingPhaseContext, {
	someConferenceCallSupportsRecording,
	SUPPORTED_CALL_PROVIDERS,
} from "../../MeetingPhaseContext";
import { setSchedulingMeetingAssistantConfigValue } from "../../StreamSettings";
import { useAutoRecordReason } from "../../StreamSettings/Automations/StreamAutoRecordAutomationConfig";
import { autoShareIsSetToEveryone, enableAutoShareToEveryone } from "../../SuggestedActions/AutoSendToEveryone";
import { useRecordingErrorIndicatorFeatureFlagOn } from "../../utils";

import type { AutoRecordReasonStream } from "~/__generated__/AutoRecordReasonStream.graphql";
import type {
	StartAndAutoRecordingControls_note$data as Note,
	StartAndAutoRecordingControls_note$key,
} from "~/__generated__/StartAndAutoRecordingControls_note.graphql";
import type { StartAndAutoRecordingControls_refetchableAutoRecordReason$key } from "~/__generated__/StartAndAutoRecordingControls_refetchableAutoRecordReason.graphql";
import type { StartAndAutoRecordingControls_viewer$key } from "~/__generated__/StartAndAutoRecordingControls_viewer.graphql";

import noteHeaderStyles from "../NoteHeader.module.css";
import styles from "./StartAndAutoRecordingControls.module.css";

type ConferenceCall = NonNullable<Note["event"]>["conferenceCalls"][number];

export const RECORDING_RESTRICTED_MESSAGE = "Recording this meeting is restricted by your workspace";

function StartRecordingTooltip({ children }: React.PropsWithChildren<{}>) {
	const { phase } = useContext(MeetingPhaseContext);

	return (
		<Tooltip
			placement="top"
			maxWidth={250}
			className={styles.tooltip}
			content={
				<>
					<span className={styles.tooltipHeader}>Start recording now</span>
					<span>
						{phase === "before"
							? "This will override any auto-record settings and begin recording immediately."
							: "The Note Taker will request to join the call and take AI notes for this meeting."}
					</span>
				</>
			}
		>
			<span>{children}</span>
		</Tooltip>
	);
}

function StartRecordingButton({ conferenceCalls }: { conferenceCalls: readonly ConferenceCall[] }) {
	const { phase, startRecording, mustUpgradeBeforeRecording: mustUpgrade } = useContext(MeetingPhaseContext);

	const makeStartRecordingFn = React.useCallback(
		// FIXME: memoize; but lodash memoize is keyed by the first param which is
		// insufficient here
		(call: ConferenceCall, mediaType: "AUDIO" | "VIDEO") => () => {
			trackConversion(FLOATING_RECORDING_CONTROLS_EXPERIMENT_KEY);
			return startRecording("BOT", call, { overrideAutoRecord: true, mediaType });
		},
		[startRecording],
	);

	const [isConferenceMenuOpen, setIsConferenceMenuOpen] = useState(false);

	const buttonContent = (
		<>
			{mustUpgrade && (
				<>
					<UpgradeIcon />
					&ensp;
				</>
			)}
			<b className={styles.nowrap}>Record</b>
		</>
	);

	const audioOnlyRecordingEnabled = useFeatureIsOn("audio-only-recording");

	const conferenceCallMenuItems = useMemo(
		() =>
			conferenceCalls.map(call => {
				const mediaTypeOptions = (
					<React.Fragment key={call.url}>
						<Menu.Item onSelect={makeStartRecordingFn(call, "AUDIO")} icon={faWaveformLines}>
							Just audio &nbsp;
							<BetaLabel />
						</Menu.Item>
						<Menu.Item onSelect={makeStartRecordingFn(call, "VIDEO")} icon={faVideo}>
							Video & audio
						</Menu.Item>
					</React.Fragment>
				);

				if (audioOnlyRecordingEnabled && conferenceCalls.length === 1) {
					return mediaTypeOptions;
				}

				const providerNotSupported = !SUPPORTED_CALL_PROVIDERS.includes(call.provider);

				const providerContent = (
					<span className={cx(styles.multipleCallMenuItem, { [styles.notSupported]: providerNotSupported })}>
						<CallIcon provider={call.provider} />
						<div className={styles.content}>
							{getCallName(call.provider)}
							{providerNotSupported && <small>Not supported</small>}
						</div>
					</span>
				);

				if (!audioOnlyRecordingEnabled) {
					return (
						<Menu.Item
							key={call.url}
							onSelect={makeStartRecordingFn(call, "VIDEO")}
							disabled={providerNotSupported}
							data-test-id="start-recording-in-note-header"
						>
							{providerContent}
						</Menu.Item>
					);
				}

				return (
					<Menu.Sub key={call.url}>
						<Menu.SubTrigger>{providerContent}</Menu.SubTrigger>
						<Menu.SubContent>{mediaTypeOptions}</Menu.SubContent>
					</Menu.Sub>
				);
			}),
		[conferenceCalls, audioOnlyRecordingEnabled, makeStartRecordingFn],
	);

	if (conferenceCalls.length === 1 && !audioOnlyRecordingEnabled) {
		const call = conferenceCalls[0];
		return (
			<StartRecordingTooltip>
				<Button
					variant={phase === "started" ? "primary" : "secondary"}
					size="slim"
					data-tour-tooltip-target="record-button"
					className={cx(noteHeaderStyles.ultraSlimButton, styles.rightButton)}
					onClick={makeStartRecordingFn(call, "VIDEO")}
					data-test-id="start-recording-in-note-header"
				>
					{buttonContent}
				</Button>
			</StartRecordingTooltip>
		);
	} else {
		return (
			<Menu open={isConferenceMenuOpen} onOpenChange={setIsConferenceMenuOpen}>
				<StartRecordingTooltip>
					<Menu.Trigger asChild>
						<Button
							variant={phase === "started" ? "primary" : "secondary"}
							size="slim"
							data-tour-tooltip-target="record-button"
							className={cx(noteHeaderStyles.ultraSlimButton, styles.rightButton)}
						>
							{buttonContent}
						</Button>
					</Menu.Trigger>
				</StartRecordingTooltip>
				<Menu.Content>{conferenceCallMenuItems}</Menu.Content>
			</Menu>
		);
	}
}

export interface StartAndAutoRecordingControlsProps {
	viewer: StartAndAutoRecordingControls_viewer$key;
	note: StartAndAutoRecordingControls_note$key;
}

export default function StartAndAutoRecordingControls({
	viewer: viewerKey,
	note: noteKey,
}: StartAndAutoRecordingControlsProps) {
	const { phase, mustUpgradeBeforeRecording: mustUpgrade } = useContext(MeetingPhaseContext);

	const viewer = useFragment(
		graphql`
			fragment StartAndAutoRecordingControls_viewer on Viewer {
				hasAccessToCopilot
				meetingRecapAutoShareSettings {
					id
					sendTo
				}
				account {
					billingAccount {
						hasCopilotAddon: hasAccessToFeature(feature: meeting_recorder)
					}
				}
				...StreamAutoRecordAutomationConfig_useAutoRecordReason_viewer
			}
		`,
		viewerKey,
	);

	const note = useFragment(
		graphql`
			fragment StartAndAutoRecordingControls_note on Note {
				id
				stream {
					id
					schedulingMeetingAssistantAutomationIsActive
					...StartAndAutoRecordingControls_refetchableAutoRecordReason
				}
				event {
					hasUsersWithExternalEmail
					guid
					conferenceCalls {
						provider
						url
					}
				}
				...AutoSendToEveryone_autoShareIsSetToEveryone_note
			}
		`,
		noteKey,
	);
	const { event } = note;

	const [autoRecordReasonStream, refetchAutoRecordReason] = useRefetchableFragment<
		AutoRecordReasonStream,
		StartAndAutoRecordingControls_refetchableAutoRecordReason$key
	>(
		graphql`
			fragment StartAndAutoRecordingControls_refetchableAutoRecordReason on NoteStream
			@refetchable(queryName: "AutoRecordReasonStream") {
				...StreamAutoRecordAutomationConfig_useAutoRecordReason_stream
			}
		`,
		note.stream,
	);
	const autoRecordReason = useAutoRecordReason(autoRecordReasonStream, viewer);

	const autoSendToEveryoneIsOn = autoShareIsSetToEveryone(note);

	const [isAutoRecordToggleLoading, setIsAutoRecordToggleLoading] = useState(false);
	const [optimisticAutoRecordIsOn, setOptimisticAutoRecordIsOn] = useState(false);

	const autoRecordIsOn = note.stream.schedulingMeetingAssistantAutomationIsActive;
	const displayChecked = isAutoRecordToggleLoading ? optimisticAutoRecordIsOn : autoRecordIsOn;

	const conferenceCalls = useMemo(() => event?.conferenceCalls ?? ([] as ConferenceCall[]), [event?.conferenceCalls]);
	const willBeRecorded = useWillBeRecordedNoCacheQuery(event?.guid);

	const onToggleAutoRecord = useCallback(async () => {
		trackConversion(FLOATING_RECORDING_CONTROLS_EXPERIMENT_KEY);
		const newValue = !note.stream.schedulingMeetingAssistantAutomationIsActive;

		setOptimisticAutoRecordIsOn(newValue);
		setIsAutoRecordToggleLoading(true);

		try {
			await setSchedulingMeetingAssistantConfigValue(note.stream.id, newValue);
			if (
				newValue === true &&
				note.event?.hasUsersWithExternalEmail &&
				!autoSendToEveryoneIsOn &&
				viewer.meetingRecapAutoShareSettings?.sendTo == "ALL"
			) {
				await enableAutoShareToEveryone(note.stream.id);
			}
			refetchAutoRecordReason({}, { fetchPolicy: "store-and-network" });
		} finally {
			setIsAutoRecordToggleLoading(false);
		}
	}, [
		autoSendToEveryoneIsOn,
		note.event?.hasUsersWithExternalEmail,
		note.stream.id,
		note.stream.schedulingMeetingAssistantAutomationIsActive,
		refetchAutoRecordReason,
		viewer.meetingRecapAutoShareSettings?.sendTo,
	]);

	if (!viewer.hasAccessToCopilot || !someConferenceCallSupportsRecording(conferenceCalls)) return null;

	if (willBeRecorded === "RESTRICTED") {
		return (
			<Tooltip
				placement="top"
				disabled={isAutoRecordToggleLoading}
				maxWidth={250}
				className={styles.tooltip}
				content={
					<>
						<span className={styles.tooltipHeader}>Recording prohibited</span>
						<span>{RECORDING_RESTRICTED_MESSAGE}</span>
					</>
				}
			>
				<div className={cx(noteHeaderStyles.ultraSlimButton, styles.restrictedIcon)}>
					<FontAwesomeIcon icon={faRecordSlash} />
				</div>
			</Tooltip>
		);
	}

	if (phase !== "before" || mustUpgrade) {
		return (
			<div className={styles.singleRecordingButtonRow}>
				<ErrorIndicator />
				<StartRecordingButton conferenceCalls={conferenceCalls} />
			</div>
		);
	}

	return (
		<div className={styles.combinedRecordingButton}>
			<ErrorIndicator />
			<Tooltip
				placement="top"
				content={
					<>
						<span className={styles.tooltipHeader}>Auto-record is {autoRecordIsOn ? "on" : "off"}</span>
						{autoRecordReason && <span>{autoRecordReason}.</span>}
						{autoRecordIsOn ? (
							<span>
								The Note Taker will automatically request to join the call to record and take AI notes for this meeting.
							</span>
						) : (
							<span>
								When enabled, the Note Taker will automatically request to join the call to record and take AI notes for
								this meeting.
							</span>
						)}
					</>
				}
				disabled={isAutoRecordToggleLoading}
				maxWidth={250}
				className={styles.tooltip}
			>
				<Button
					aria-pressed={displayChecked}
					variant="secondary"
					size="slim"
					className={cx(noteHeaderStyles.ultraSlimButton, styles.leftButton)}
					onClick={onToggleAutoRecord}
					disabled={isAutoRecordToggleLoading}
					data-test-id="auto-record-in-note-header"
				>
					<RecordIconSwitch aria-hidden checked={displayChecked} rootElement="div" />
				</Button>
			</Tooltip>
			<div className={styles.divider} />
			<StartRecordingButton conferenceCalls={conferenceCalls} />
		</div>
	);
}

const ErrorIndicator = () => {
	const { previousErrorIndicator: indicator, viewerId, hasMeetingRecordings } = useContext(MeetingPhaseContext);

	const errorIndicatorFeatureFlagOn = useRecordingErrorIndicatorFeatureFlagOn();
	const formatName = useCallback((name: string) => {
		// We only want the first name and the first character of the last name
		const names = name.split(" ");
		return `${names[0]} ${names[names.length - 1][0]}`; // Incase we have a middle name
	}, []);

	const formatDate = useCallback((date: Date) => {
		// Example: Oct 28 at 2:01 PM

		// Use client timezone to format the date
		const monthAndDay = date.toLocaleString(undefined, { month: "short", day: "numeric" });
		const time = date.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
		return `${monthAndDay} at ${time}`;
	}, []);

	const tooltipContent = useMemo(() => {
		if (!indicator) return null;
		const message =
			indicator.type == "cancelled" && indicator.cancelledBy
				? `Cancelled in Fellow by ${formatName(indicator.cancelledBy.fullName)}`
				: indicator.message
					? indicator.message
					: "An error occurred while recording the meeting";
		return (
			<div className={styles.errorIndicatorTooltipContent}>
				<span className={styles.errorIndicatorMessage}>{message}</span>
				{indicator.updatedAt && (
					<span className={styles.errorIndicatorUpdatedAt}>{formatDate(indicator.updatedAt)}</span>
				)}
			</div>
		);
	}, [indicator, formatName, formatDate]);

	if (!indicator || hasMeetingRecordings) return null;

	if (indicator.type === "cancelled" && indicator.cancelledBy?.id === viewerId) {
		return null;
	}

	if (!errorIndicatorFeatureFlagOn) return null;

	return (
		<div className={styles.errorIndicator}>
			<Tooltip placement="top" content={tooltipContent}>
				<FontAwesomeIcon icon={faTriangleExclamation} className={styles.errorIndicatorIcon} />
			</Tooltip>
		</div>
	);
};
